import {
  BOARD_COLUMNS,
  DEAL_HEALTH,
  INITIAL_PHASES_FOR,
  PHASE_LABELS,
  PHASES_FOR,
  PROGRESS_BY_PHASE,
  RESOLVED_ROLE_LABELS,
} from "@/constant/deals.constant";
import {
  formatCurrency,
  formatUtcDate,
  parseIsoDate,
  utcDayDiff,
} from "@/utils/common.utils";
import { Audience, DealPhase, DealRole, DealType, Side, SnapshotSource, TaskStatus } from "@/enum/common.enum";
import { DEFAULT_COUNTRY } from "@/lib/phone";
import type { DealExportRow } from "@/types/deals.types";

type AnyRecord = Record<string, any>;

export {
  BOARD_COLUMNS,
  DEAL_HEALTH,
  INITIAL_PHASES_FOR,
  LOAN_LABELS,
  PHASE_LABELS,
  PHASE_LABELS_CLIENT_FOR,
  PHASES_FOR,
  PROGRESS_BY_PHASE,
} from "@/constant/deals.constant";

export const createTaskForm = (phase = "pre_listing") => ({
  title: "", assigned_role: "agent", phase, due_date: "", description: "", priority: "medium",
  due_date_source: "manual", due_date_offset_days: 3,
  // Matches how the seeded checklist behaves: the client reads the list, so a
  // row the agent adds by hand belongs in it unless they say otherwise.
  audience: "client", client_release: "auto", after_task_id: undefined,
  request_type: "none",
});

const REQUEST_TYPES = ["action", "signature", "document", "info"] as const;
export type RequestType = (typeof REQUEST_TYPES)[number];

/** The form uses "none" because a Radix Select item cannot have an empty value. */
export const requestTypeToPayload = (value: string | null | undefined): RequestType | null =>
  (REQUEST_TYPES as readonly string[]).includes(value ?? "") ? (value as RequestType) : null;

/** An extra contract contingency on a listing, pre-aimed at the empty slot the
 *  seller's under-contract checklist reserves for it. `anchorTaskId` is the row
 *  it should sit directly beneath — "We've cleared the due diligence period". */
export const createContingencyForm = (anchorTaskId?: string) => ({
  ...createTaskForm("under_contract"),
  after_task_id: anchorTaskId,
});

/** Who sees a document by default, from who it belongs to — the same rule the
 *  server applies when no audience is sent. The agent can still change it. */
export const documentAudienceForOwner = (ownerRole: string): Audience =>
  ownerRole === "client" ? Audience.CLIENT
    : ownerRole === "lender" || ownerRole === "attorney" ? Audience.EXTERNALS
    : Audience.INTERNAL;

export const createDocumentForm = () => ({
  name: "", category: "Contract", owner_role: "agent", url: "", storage_path: null,
  content_type: null, size: null, original_filename: null, audience: Audience.INTERNAL,
});

export const createParticipantForm = () => ({
  // Client is far and away the most common person to bring into a deal room.
  name: "", email: "", role: "client", company: "", phone: "",
  phone_country: DEFAULT_COUNTRY, send_sms: false,
});

export const isScheduledTask = (task: AnyRecord): boolean => /schedul/i.test(task?.title || "");
export const sideOf = (deal: AnyRecord): DealType => (
  deal?.deal_type === DealType.BUY ? DealType.BUY : DealType.SELL
);

export function resolveRole({ role, side, isInternalHost, hostDealType }: AnyRecord): string | null {
  if (!role) return null;
  if (role === DealRole.CLIENT) {
    if (side === Side.BUY) return "buyer_client";
    if (side === Side.SELL) return "seller_client";
    if (hostDealType === DealType.SELL) return "seller_client";
    return "buyer_client";
  }
  if (role === DealRole.AGENT) {
    if (isInternalHost) return hostDealType === DealType.SELL ? "listing_agent" : "buyer_agent";
    if (side === Side.BUY) return "buyer_agent";
    if (side === Side.SELL) return "listing_agent";
    return "agent";
  }
  if (role === DealRole.COORDINATOR) return "transaction_coordinator";
  if (role === DealRole.ATTORNEY) return "closing_attorney";
  if (role === DealRole.LENDER) return "lender";
  if (role === DealRole.CO_AGENT) {
    if (side === Side.BUY) return "buyer_agent";
    if (side === Side.SELL) return "listing_agent";
    return "co_agent";
  }
  return role;
}

export function roleLabel(resolvedOrRaw: unknown): string {
  const role = String(resolvedOrRaw || "");
  return RESOLVED_ROLE_LABELS[role] || role.replace(/_/g, " ");
}

export function getDealPhases(deal: AnyRecord): readonly string[] {
  return PHASES_FOR[sideOf(deal)] || [];
}

/**
 * Phase chips for the timeline filter, for *this* deal's pathway.
 *
 * Replaces a hardcoded list that named only the buy-side phases, so on a
 * listing there was no way to filter to Pre-listing or Listing — the two
 * phases that deal actually spends most of its life in. Derived from
 * `INITIAL_PHASES_FOR`, which is the set that carries seeded tasks; `closed`
 * is excluded because nothing is ever seeded into it.
 */
export function taskPhaseFilters(deal: AnyRecord): Array<{ value: string; label: string }> {
  const phases = INITIAL_PHASES_FOR[sideOf(deal)] || [];
  return [
    { value: "", label: "All" },
    ...phases.map((phase) => ({ value: phase, label: PHASE_LABELS[phase] || phase })),
  ];
}

/**
 * The phase a deal takes on when its card is dropped into `columnId`.
 *
 * Board columns are pathway-agnostic ("Opportunities"), phases are not
 * (`pre_listing` for a listing, `pre_approval` for a buyer), so the column's
 * `phases` tuple is indexed by the deal's own pathway. Returns null for an
 * unknown column, or when the deal is already in that column — the caller
 * treats that as "nothing to do" rather than firing a no-op PATCH.
 */
export function phaseForColumn(deal: AnyRecord, columnId: string): DealPhase | null {
  const column = BOARD_COLUMNS.find((c) => c.id === columnId);
  if (!column) return null;
  // The `as` is the one bridge between BOARD_COLUMNS' literal tuples and the
  // DealPhase enum; every entry in `phases` is a DealPhase member by construction.
  const phase = (column.phases[sideOf(deal) === DealType.BUY ? 1 : 0]
    ?? column.phases[0]) as DealPhase | undefined;
  return phase && phase !== deal.phase ? phase : null;
}

export function getTaskProgress(tasks: AnyRecord[] = []) {
  const completed = tasks.filter((task) => task.status === TaskStatus.COMPLETE).length;
  return { completed, total: tasks.length, percentage: tasks.length ? Math.round((completed / tasks.length) * 100) : 0 };
}

export function hasDocumentSource(document: AnyRecord): boolean {
  return Boolean(document?.storage_path || document?.url);
}

export function mergeUploadedDocument(current: AnyRecord, upload: AnyRecord): AnyRecord {
  return {
    ...current,
    storage_path: upload.path,
    content_type: upload.content_type,
    size: upload.size,
    original_filename: upload.original_filename,
    name: current.name || (upload.original_filename || "").replace(/\.[^.]+$/, ""),
  };
}

export function formatMoney(value: unknown): string | null {
  return formatCurrency(value);
}

export function formatCloseDate(value: unknown): string | null {
  if (!value) return null;
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function dealHealth(deal: AnyRecord): string {
  const raw = deal.deal_health || deal.health || "on_track";
  return DEAL_HEALTH[raw] ? raw : "on_track";
}

export function dealProgress(deal: AnyRecord): number {
  if (typeof deal.progress_pct === "number") return deal.progress_pct;
  return PROGRESS_BY_PHASE[deal.phase] ?? 0;
}

export function isClosingSoon(deal: AnyRecord, days = 14): boolean {
  const date = parseIsoDate(deal?.contract_terms?.closing_date || deal?.close_date, true);
  if (!date) return false;
  const difference = utcDayDiff(date);
  return difference !== null && difference >= 0 && difference <= days;
}

/**
 * CSV rows for the Deals export.
 *
 * Takes rows from `GET /deals/export`, which already resolves the stage label
 * and applies the `contract_price || list_price` fallback server-side. The
 * header order is unchanged, so files stay comparable with older exports.
 */
export function dealExportRows(rows: DealExportRow[]): unknown[][] {
  return [
    ["Address", "Client", "Side", "Stage", "Price", "Agent"],
    ...rows.map((row) => [
      row.address, row.client_name, row.deal_type, row.stage, row.price, row.agent_name,
    ]),
  ];
}

export function formatDealPrice(value: string | number | null | undefined): string {
  if (!value) return "Not provided";
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  }).format(Number(value));
}

export function formatPrintableMoney(value: unknown): string {
  return formatCurrency(value, { fallback: "—" }) || "—";
}

export function formatPrintableDate(value: unknown): string {
  return formatUtcDate(value, { weekday: "long", month: "long", year: "numeric" }, "—");
}

/**
 * Whole calendar days between two dates, on the LOCAL calendar — the twin of
 * `utcDayDiff` for callers that hold ISO strings rather than Dates. Both ends
 * are normalised to local midnight, so "today" is 0 at any hour and in any
 * timezone.
 */
export function daysBetweenIso(fromIso: unknown, toIso: unknown): number | null {
  if (!fromIso || !toIso) return null;
  const dayStart = (value: unknown) => {
    const raw = String(value);
    if (raw.length === 10) {
      const [year, month, day] = raw.split("-").map(Number);
      const bare = new Date(year, month - 1, day);
      return Number.isNaN(bare.getTime()) ? null : bare.getTime();
    }
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  };
  const from = dayStart(fromIso);
  const to = dayStart(toIso);
  if (from === null || to === null) return null;
  const days = Math.round((to - from) / 86_400_000);
  return Number.isFinite(days) ? days : null;
}

export function formatDealDateTime(value: unknown): string {
  if (!value) return "—";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  const datePart = date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  if (date.getHours() === 0 && date.getMinutes() === 0) return datePart;
  const timePart = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${datePart} · ${timePart}`;
}

export function getDealSummaryDefaults(deal: AnyRecord, terms: AnyRecord): AnyRecord {
  return {
    purchase_price: terms.purchase_price ?? "", closing_date: terms.closing_date ?? "",
    due_diligence_end_date: terms.due_diligence_end_date ?? "",
    earnest_money_deposit: terms.earnest_money_deposit ?? "",
    financing_contingency_days: terms.financing_contingency_days ?? "",
    financing_contingency_date: terms.financing_contingency_date ?? "",
    financing_contingency_mode: terms.financing_contingency_date ? "date" : "days",
    purchase_method: terms.purchase_method ?? (deal?.financing || "financed"),
    loan_type: terms.loan_type ?? "conventional", special_stipulations: terms.special_stipulations ?? "",
    home_sale_contingency_enabled: Boolean(terms.home_sale_contingency_enabled),
    home_sale_contingency_address: terms.home_sale_contingency_address ?? "",
    kickout_clause_enabled: Boolean(terms.kickout_clause_enabled),
    kickout_clause_details: terms.kickout_clause_details ?? "", closing_address: terms.closing_address ?? "",
    closing_datetime: terms.closing_datetime ? toLocalDateTimeInput(terms.closing_datetime) : "",
    closing_contact_name: terms.closing_contact_name ?? "", closing_contact_phone: terms.closing_contact_phone ?? "",
    ...getContractPromptDefaults(terms, deal),
  };
}

/** Every client on the agent's side, as a contract names them: "Michael Saar &
 *  Joann Saar". Co-buyers and co-sellers sign too (client feedback, item 3). */
export const dealClientsLabel = (deal: AnyRecord = {}): string =>
  [...new Set([deal.client_name, ...(deal.additional_clients || []).map((c: AnyRecord) => c.name)].filter(Boolean))].join(" & ");

/** The agent's own side: what was saved, unless it is only the primary client
 *  on a deal that has co-clients — the old single-pick dropdown saved exactly
 *  that, so it is the one value known to have dropped somebody. */
const ownSideNames = (saved: unknown, deal: AnyRecord): string => {
  const value = String(saved || "").trim();
  return !value || ((deal.additional_clients || []).length && value === deal.client_name) ? dealClientsLabel(deal) : value;
};

/** Defaults for the ten terms the under-contract prompt added. Split out so the
 *  prompt and the full editor seed them identically. Party names fall back to
 *  what the deal already knows (its clients and agent) so they are not retyped;
 *  `||` rather than `??` because the prompt has always saved blanks as "". */
export function getContractPromptDefaults(terms: AnyRecord, deal: AnyRecord = {}): AnyRecord {
  const isSell = deal.deal_type === DealType.SELL;
  const isBuy = deal.deal_type === DealType.BUY;
  return {
    earnest_money_holder: terms.earnest_money_holder ?? "",
    option_fee_amount: terms.option_fee_amount ?? "",
    earnest_money_due_date: terms.earnest_money_due_date ?? "",
    option_fee_due_date: terms.option_fee_due_date ?? "",
    // The agent's own side starts with every client on the deal; the other
    // side keeps whatever was typed. Both are free text.
    seller_names: isSell ? ownSideNames(terms.seller_names, deal) : terms.seller_names || "",
    buyer_names: isBuy ? ownSideNames(terms.buyer_names, deal) : terms.buyer_names || "",
    inspection_deadline_1: terms.inspection_deadline_1 ?? "",
    inspection_deadline_2: terms.inspection_deadline_2 ?? "",
    inspection_company: terms.inspection_company ?? "",
    inspection_date: terms.inspection_date ?? "",
    inspection_notes: terms.inspection_notes ?? "",
    inspection_start_time: terms.inspection_start_time ?? "",
    inspection_end_time: terms.inspection_end_time ?? "",
    possession_datetime: terms.possession_datetime ? toLocalDateTimeInput(terms.possession_datetime) : "",
    utility_connect_date: terms.utility_connect_date ?? "",
    utility_disconnect_date: terms.utility_disconnect_date ?? "",
    closing_law_firm: terms.closing_law_firm ?? "",
    listing_agent: terms.listing_agent || (isSell ? deal.agent_name || "" : ""),
    listing_brokerage: terms.listing_brokerage ?? "",
    coop_brokerage: terms.coop_brokerage ?? "",
    coop_agent: terms.coop_agent || (!isSell && deal.deal_type ? deal.agent_name || "" : ""),
    further_considerations: terms.further_considerations ?? "",
  };
}

/** "09:00" → "9 am", "13:30" → "1:30 pm". Anything unparseable passes through. */
export function formatClockTime(value: unknown): string {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(value || ""));
  if (!match) return String(value || "");
  const hours = Number(match[1]);
  const suffix = hours < 12 ? "am" : "pm";
  const twelve = hours % 12 || 12;
  return match[2] === "00" ? `${twelve} ${suffix}` : `${twelve}:${match[2]} ${suffix}`;
}

/** The inspection's arrival window: "9 am – 12 pm", "at 9 am", or "". */
export function formatInspectionWindow(terms: AnyRecord = {}): string {
  const start = terms.inspection_start_time;
  const end = terms.inspection_end_time;
  if (start && end) return `${formatClockTime(start)} – ${formatClockTime(end)}`;
  if (start) return `at ${formatClockTime(start)}`;
  if (end) return `by ${formatClockTime(end)}`;
  return "";
}

/** The checklist row that records the inspection being booked, on the deal's
 *  own side: "Your inspections are scheduled" (buyer) or "The buyer's
 *  inspections are underway" (seller). */
export function findInspectionTask(tasks: AnyRecord[] = [], deal: AnyRecord = {}): AnyRecord | null {
  const side = deal.deal_type === DealType.BUY ? "buy" : "sell";
  return tasks.find((task) =>
    /inspections? (are|is) (scheduled|underway)/i.test(task.title || "")
    && (!task.side || task.side === side || task.side === "neutral")
    && !task.waived_at && task.is_active !== false,
  ) || null;
}

/** Local "YYYY-MM-DD" + "HH:MM" → the UTC ISO instant the checklist stores. */
export function localDateTimeToIso(date: string, time = ""): string | null {
  if (!date) return null;
  const parsed = new Date(`${date}T${time || "09:00"}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

/** A stored UTC instant → the agent's local `{ date, time }`. */
export function isoToLocalDateTime(value: unknown): { date: string; time: string } {
  const local = toLocalDateTimeInput(value);
  return local ? { date: local.slice(0, 10), time: local.slice(11, 16) } : { date: "", time: "" };
}

export function toLocalDateTimeInput(value: unknown): string {
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return "";
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function toIsoUtc(value: unknown): string | null {
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function createSnapshotStep(source: AnyRecord = {}, index: string | number = Date.now()) {
  return {
    id: source.task_id || source.id || `step-${index}`, label: source.label || "",
    owner_role: source.owner_role || "unassigned", due_date: source.due_date || "",
    status: source.status || "not_started",
  };
}

export function getSnapshotDefaults(snapshot: AnyRecord) {
  return {
    health: snapshot.health_source === SnapshotSource.AGENT ? snapshot.health : "",
    note: snapshot.status_note || "", auto_steps: snapshot.next_steps_source !== SnapshotSource.AGENT,
    steps: (snapshot.next_steps || []).slice(0, 3).map(createSnapshotStep),
  };
}

/** A street address typed into a client-name field — the 1033 Quail Cove Dr.
 *  seller. Names don't start with a house number, and one that matches the
 *  property address is plainly the wrong field. */
export function looksLikeAddress(name: unknown, address: unknown = ""): boolean {
  const value = String(name || "").trim().toLowerCase();
  if (!value) return false;
  return /^\d+\s+\S/.test(value) || value === String(address || "").trim().toLowerCase();
}

/** True when any client name on a new-deal draft needs fixing before it can be created. */
export function clientNamesNeedReview(form: AnyRecord): boolean {
  return [form.client_name, ...(form.additional_clients || []).map((c: AnyRecord) => c.name)]
    .some((name) => looksLikeAddress(name, form.address));
}
